<template>
  <v-col cols="12">
    <Loader
      v-if="loading"
      :key="generateUniqueId()"
      :loader-state="loading"
    ></Loader>

    <v-card>
      <v-card-title class="align-center">
        Traductions automatiques
        <v-spacer></v-spacer>
        <v-btn
          small
          color="info"
          :loading="loading"
          @click="refresh()"
        >
          <v-icon
            small
            class="mr-1"
          >
            {{ icons.mdiRefresh }}
          </v-icon>
          Actualiser la liste
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-row class="summary-row">
          <v-col
            v-for="item in summaryCards"
            :key="item.label"
            cols="12"
            sm="6"
            md="3"
          >
            <div class="summary-tile">
              <div class="text-caption text-uppercase">
                {{ item.label }}
              </div>
              <div class="text-h6 font-weight-bold">
                {{ item.value }}
              </div>
            </div>
          </v-col>
        </v-row>

        <v-row class="summary-row mt-1">
          <v-col
            v-for="item in workloadCards"
            :key="item.label"
            cols="12"
            sm="6"
            md="3"
          >
            <div class="summary-tile workload-tile">
              <div class="text-caption text-uppercase">
                {{ item.label }}
              </div>
              <div class="text-h6 font-weight-bold">
                {{ item.value }}
              </div>
            </div>
          </v-col>
        </v-row>

        <v-row
          v-if="summary"
          class="mt-3"
        >
          <v-col
            cols="12"
            md="6"
          >
            <div class="stats-panel">
              <div class="stats-title">
                Reste à traduire par langue
              </div>
              <v-simple-table dense>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th>Locale</th>
                      <th class="text-right">
                        Jobs
                      </th>
                      <th class="text-right">
                        Champs
                      </th>
                      <th class="text-right">
                        Caractères
                      </th>
                      <th class="text-right">
                        En cours
                      </th>
                      <th class="text-right">
                        À relancer
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="row in remainingLocaleRows"
                      :key="row.locale"
                    >
                      <td class="font-weight-bold">
                        {{ row.locale }}
                      </td>
                      <td class="text-right">
                        {{ formatNumber(row.jobCount) }}
                      </td>
                      <td class="text-right">
                        {{ formatNumber(row.fieldCount) }}
                      </td>
                      <td class="text-right">
                        {{ formatNumber(row.charCount) }}
                      </td>
                      <td class="text-right">
                        {{ formatNumber(row.activeJobCount) }}
                      </td>
                      <td class="text-right">
                        <v-chip
                          x-small
                          :color="row.retryableJobCount > 0 ? 'error' : 'success'"
                          dark
                        >
                          {{ formatNumber(row.retryableJobCount) }}
                        </v-chip>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </div>
          </v-col>

          <v-col
            cols="12"
            md="6"
          >
            <div class="stats-panel">
              <div class="stats-title">
                Reste à traduire par contenu
              </div>
              <div class="pa-3">
                <v-select
                  v-model="remainingTypeLocale"
                  dense
                  outlined
                  hide-details
                  label="Langue des statistiques"
                  :items="remainingTypeLocaleItems"
                  item-text="label"
                  item-value="value"
                ></v-select>
              </div>
              <v-simple-table
                dense
                fixed-header
                height="320"
              >
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th class="text-right">
                        Jobs
                      </th>
                      <th class="text-right">
                        Champs
                      </th>
                      <th class="text-right">
                        Caractères
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="row in remainingTypeRows"
                      :key="row.entity || row.type"
                    >
                      <td class="font-weight-bold">
                        {{ row.type }}
                      </td>
                      <td class="text-right">
                        {{ formatNumber(row.jobCount) }}
                      </td>
                      <td class="text-right">
                        {{ formatNumber(row.fieldCount) }}
                      </td>
                      <td class="text-right">
                        {{ formatNumber(row.charCount) }}
                      </td>
                    </tr>
                    <tr v-if="!remainingTypeRows.length">
                      <td
                        colspan="4"
                        class="text-center"
                      >
                        Aucun champ manquant
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </div>
          </v-col>
        </v-row>

        <div class="manual-panel mt-3">
          <div class="stats-title">
            Déclenchement manuel
          </div>
          <v-row
            class="pa-3"
            align="center"
          >
            <v-col
              cols="12"
              sm="6"
              md="2"
            >
              <v-select
                v-model="manualForm.locale"
                dense
                outlined
                hide-details
                label="Locale"
                :items="configuredLocales"
              ></v-select>
            </v-col>
            <v-col
              cols="12"
              sm="6"
              md="3"
            >
              <v-select
                v-model="manualForm.entity"
                dense
                outlined
                hide-details
                clearable
                label="Entité"
                :items="configuredEntities"
                item-text="label"
                item-value="value"
              ></v-select>
            </v-col>
            <v-col
              cols="12"
              sm="6"
              md="2"
            >
              <v-text-field
                v-model.number="manualForm.limit"
                dense
                outlined
                hide-details
                type="number"
                min="0"
                label="Jobs max"
              ></v-text-field>
            </v-col>
            <v-col
              cols="12"
              sm="6"
              md="2"
            >
              <v-checkbox
                v-model="manualForm.retryFailed"
                dense
                hide-details
                label="Relancer les échecs"
              ></v-checkbox>
            </v-col>
            <v-col
              cols="12"
              sm="6"
              md="2"
            >
              <v-checkbox
                v-model="manualForm.oversized"
                dense
                hide-details
                label="Oversized"
              ></v-checkbox>
            </v-col>
            <v-col
              cols="12"
              md="12"
            >
              <v-btn
                small
                color="info"
                class="mr-2"
                :loading="previewLoading"
                :disabled="!manualForm.locale"
                @click="previewManualBatch()"
              >
                <v-icon
                  small
                  class="mr-1"
                >
                  {{ icons.mdiDatabaseSearchOutline }}
                </v-icon>
                Prévisualiser
              </v-btn>
              <v-btn
                small
                color="success"
                :loading="generateLoading"
                :disabled="!manualForm.locale"
                @click="generateManualBatch()"
              >
                <v-icon
                  small
                  class="mr-1"
                >
                  {{ icons.mdiRefresh }}
                </v-icon>
                Envoyer OpenAI
              </v-btn>
            </v-col>
          </v-row>

          <v-simple-table
            v-if="manualResultRows.length"
            dense
          >
            <template v-slot:default>
              <thead>
                <tr>
                  <th>Locale</th>
                  <th>Mode</th>
                  <th class="text-right">
                    Jobs
                  </th>
                  <th class="text-right">
                    Requests
                  </th>
                  <th class="text-right">
                    Champs
                  </th>
                  <th class="text-right">
                    Caractères
                  </th>
                  <th class="text-right">
                    Bloqués
                  </th>
                  <th>Résultat</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in manualResultRows"
                  :key="row.locale + row.mode + row.result"
                >
                  <td>{{ row.locale }}</td>
                  <td>{{ row.mode }}</td>
                  <td class="text-right">
                    {{ formatNumber(row.jobs) }}
                  </td>
                  <td class="text-right">
                    {{ formatNumber(row.requests) }}
                  </td>
                  <td class="text-right">
                    {{ formatNumber(row.fields) }}
                  </td>
                  <td class="text-right">
                    {{ formatNumber(row.chars) }}
                  </td>
                  <td class="text-right">
                    {{ formatNumber(row.blocked) }}
                  </td>
                  <td>{{ row.result }}</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </div>

        <v-row
          class="mt-2"
          align="center"
        >
          <v-col
            cols="12"
            md="4"
          >
            <v-text-field
              v-model="searchText"
              dense
              outlined
              hide-details
              label="Recherche"
              @keyup.enter="search()"
            ></v-text-field>
          </v-col>
          <v-col
            cols="12"
            sm="6"
            md="2"
          >
            <v-select
              v-model="locale"
              dense
              outlined
              hide-details
              clearable
              label="Locale"
              :items="locales"
              @change="search()"
            ></v-select>
          </v-col>
          <v-col
            cols="12"
            sm="6"
            md="3"
          >
            <v-select
              v-model="status"
              dense
              outlined
              hide-details
              clearable
              label="Statut"
              :items="statusItems"
              item-text="label"
              item-value="value"
              @change="search()"
            ></v-select>
          </v-col>
          <v-col
            cols="12"
            md="3"
          >
            <v-btn
              small
              color="info"
              class="mr-2"
              @click="search()"
            >
              <v-icon
                small
                class="mr-1"
              >
                {{ icons.mdiDatabaseSearchOutline }}
              </v-icon>
              Rechercher
            </v-btn>
            <v-btn
              small
              color="info"
              @click="reset()"
            >
              <v-icon
                small
                class="mr-1"
              >
                {{ icons.mdiBackupRestore }}
              </v-icon>
              Réinitialiser
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>

      <v-simple-table>
        <template v-slot:default>
          <thead>
            <tr>
              <th class="text-uppercase">
                Batch
              </th>
              <th class="text-center text-uppercase">
                Locale
              </th>
              <th class="text-center text-uppercase">
                Mode
              </th>
              <th class="text-center text-uppercase">
                Statut
              </th>
              <th class="text-center text-uppercase">
                Jobs
              </th>
              <th class="text-center text-uppercase">
                Champs
              </th>
              <th class="text-center text-uppercase">
                Caractères
              </th>
              <th class="text-center text-uppercase">
                Erreurs
              </th>
              <th class="text-center text-uppercase">
                Créé le
              </th>
              <th class="text-center text-uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="batch in batches"
              :key="batch.id"
            >
              <td>
                <div class="font-weight-bold batch-id">
                  {{ batch.openAiBatchId }}
                </div>
                <div class="text-caption muted-text">
                  {{ batch.batchFile }}
                </div>
              </td>
              <td class="text-center">
                {{ batch.targetLocale || '-' }}
              </td>
              <td class="text-center">
                {{ batch.mode || '-' }}
              </td>
              <td class="text-center">
                <v-chip
                  small
                  :color="statusColor(batch.status)"
                  dark
                >
                  {{ statusLabel(batch.status) }}
                </v-chip>
              </td>
              <td class="text-center">
                {{ formatNumber(batch.jobCount) }}
              </td>
              <td class="text-center">
                {{ formatNumber(batch.fieldCount) }}
              </td>
              <td class="text-center">
                {{ formatNumber(batch.charCount) }}
              </td>
              <td class="text-center">
                <v-chip
                  small
                  :color="batch.errorCount > 0 ? 'error' : 'success'"
                  dark
                >
                  {{ formatNumber(batch.errorCount) }}
                </v-chip>
              </td>
              <td class="text-center">
                {{ formatDate(batch.createdAt) }}
              </td>
              <td class="text-center text-no-wrap">
                <v-tooltip top>
                  <template #activator="{ on, attrs }">
                    <v-btn
                      color="warning"
                      fab
                      dark
                      x-small
                      :to="{ name: 'autoTranslationBatchDetails', params: { batchId: batch.id }}"
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-icon dark>
                        {{ icons.mdiEye }}
                      </v-icon>
                    </v-btn>
                  </template>
                  <span>Consulter</span>
                </v-tooltip>
                <v-tooltip
                  v-if="canRefreshBatch(batch)"
                  top
                >
                  <template #activator="{ on, attrs }">
                    <v-btn
                      color="info"
                      fab
                      dark
                      x-small
                      class="ml-1"
                      aria-label="Vérifier le statut OpenAI"
                      :loading="isBatchActionLoading(batch, 'refresh')"
                      v-bind="attrs"
                      v-on="on"
                      @click="refreshBatch(batch)"
                    >
                      <v-icon dark>
                        {{ icons.mdiRefresh }}
                      </v-icon>
                    </v-btn>
                  </template>
                  <span>Vérifier le statut OpenAI</span>
                </v-tooltip>
                <v-tooltip
                  v-if="canImportBatch(batch)"
                  top
                >
                  <template #activator="{ on, attrs }">
                    <v-btn
                      color="success"
                      fab
                      dark
                      x-small
                      class="ml-1"
                      aria-label="Importer les résultats valides"
                      :loading="isBatchActionLoading(batch, 'import')"
                      v-bind="attrs"
                      v-on="on"
                      @click="importBatch(batch)"
                    >
                      <v-icon dark>
                        {{ icons.mdiDatabaseSearchOutline }}
                      </v-icon>
                    </v-btn>
                  </template>
                  <span>Importer les résultats valides</span>
                </v-tooltip>
                <v-tooltip
                  v-if="canRetryBatch(batch)"
                  top
                >
                  <template #activator="{ on, attrs }">
                    <v-btn
                      color="warning"
                      fab
                      dark
                      x-small
                      class="ml-1"
                      aria-label="Relancer les échecs de ce batch"
                      v-bind="attrs"
                      v-on="on"
                      @click="openBatchRetry(batch)"
                    >
                      <v-icon dark>
                        {{ icons.mdiReplay }}
                      </v-icon>
                    </v-btn>
                  </template>
                  <span>Relancer les échecs de ce batch</span>
                </v-tooltip>
              </td>
            </tr>
          </tbody>
          <tbody v-if="!batches.length">
            <tr>
              <td colspan="10">
                <span v-if="loading === false">Aucun élément.</span>
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>

      <v-card-actions
        v-if="hasMore"
        class="justify-center"
      >
        <v-btn
          small
          color="info"
          :loading="loading"
          @click="getBatches(true)"
        >
          Charger plus
        </v-btn>
      </v-card-actions>
    </v-card>
    <v-dialog
      v-model="retryDialog"
      max-width="640"
      :persistent="retrySubmitting"
    >
      <v-card v-if="retryBatch">
        <v-card-title>Relancer les échecs</v-card-title>
        <v-card-text>
          <div class="retry-batch-id mb-2">
            {{ retryBatch.openAiBatchId }}
          </div>
          <div class="mb-3">
            {{ retryBatch.targetLocale }} · {{ retryBatch.mode }}
          </div>
          <v-alert
            v-if="retryBatch.status === 'output_unavailable' && retryBatch.errorMessage"
            type="warning"
            dense
            outlined
          >
            {{ retryBatch.errorMessage }}
          </v-alert>
          <v-progress-linear
            v-if="retryLoading"
            indeterminate
            color="info"
          ></v-progress-linear>
          <v-alert
            v-else-if="retryError"
            type="error"
            dense
            outlined
          >
            {{ retryError }}
          </v-alert>
          <template v-else-if="retryResult">
            <v-alert
              v-if="!retryHasJobs"
              type="info"
              dense
              outlined
            >
              {{ retryResult.rows.some(row => row.result === 'empty-plan') ? 'Aucune requête planifiable avec le mode et les limites actuels.' : 'Aucun champ à relancer pour ce batch.' }}
            </v-alert>
            <v-simple-table
              v-else
              dense
            >
              <template #default>
                <tbody>
                  <tr>
                    <th>Jobs à relancer</th>
                    <td class="text-right">
                      {{ formatNumber(retryTotals.jobs) }}
                    </td>
                  </tr>
                  <tr>
                    <th>Champs</th>
                    <td class="text-right">
                      {{ formatNumber(retryTotals.fields) }}
                    </td>
                  </tr>
                  <tr>
                    <th>Caractères</th>
                    <td class="text-right">
                      {{ formatNumber(retryTotals.chars) }}
                    </td>
                  </tr>
                  <tr>
                    <th>Requêtes OpenAI</th>
                    <td class="text-right">
                      {{ formatNumber(retryTotals.requests) }}
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </template>
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn
            text
            :disabled="retrySubmitting"
            @click="retryDialog = false"
          >
            Fermer
          </v-btn>
          <v-btn
            color="success"
            :loading="retrySubmitting"
            :disabled="retryLoading || Boolean(retryError) || !retryHasJobs"
            @click="submitBatchRetry()"
          >
            <v-icon
              small
              class="mr-1"
            >
              {{ icons.mdiReplay }}
            </v-icon>
            Envoyer OpenAI
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-col>
</template>

<script>
import { mdiBackupRestore, mdiDatabaseSearchOutline, mdiEye, mdiRefresh, mdiReplay } from '@mdi/js'
import Vue from 'vue'
import config from '../../config'
import Loader from '@/components/Common/Loader'

export default {
  components: {
    Loader
  },
  data: () => ({
    loading: false,
    summaryLoading: false,
    batches: [],
    summary: null,
    remainingTypeLocale: '',
    page: 1,
    limit: 30,
    hasMore: false,
    searchText: '',
    locale: null,
    status: null,
    previewLoading: false,
    generateLoading: false,
    manualResult: null,
    batchActionLoading: {},
    retryDialog: false,
    retryBatch: null,
    retryLoading: false,
    retrySubmitting: false,
    retryResult: null,
    retryError: '',
    retryRequestId: 0,
    manualForm: {
      locale: 'nl',
      entity: null,
      limit: 100,
      retryFailed: false,
      oversized: false
    },
    locales: ['en', 'nl', 'de'],
    statusItems: [
      { value: 'submitted', label: 'Soumis' },
      { value: 'validating', label: 'Validation OpenAI' },
      { value: 'in_progress', label: 'En cours' },
      { value: 'finalizing', label: 'Finalisation' },
      { value: 'cancelling', label: 'Annulation en cours' },
      { value: 'expired', label: 'Expiré' },
      { value: 'cancelled', label: 'Annulé' },
      { value: 'completed', label: 'Terminé OpenAI' },
      { value: 'downloaded', label: 'Téléchargé' },
      { value: 'output_unavailable', label: 'Résultats indisponibles' },
      { value: 'validated', label: 'Validé' },
      { value: 'validation_failed', label: 'Validation échouée' },
      { value: 'imported', label: 'Importé' },
      { value: 'partially_imported', label: 'Import partiel' },
      { value: 'import_failed', label: 'Import échoué' },
      { value: 'failed', label: 'Échec' }
    ],
    icons: {
      mdiBackupRestore,
      mdiDatabaseSearchOutline,
      mdiEye,
      mdiRefresh,
      mdiReplay
    }
  }),
  computed: {
    retryTotals () {
      const rows = this.retryResult ? this.retryResult.rows : []

      return rows.reduce((totals, row) => ({
        jobs: totals.jobs + row.jobs,
        fields: totals.fields + row.fields,
        chars: totals.chars + row.chars,
        requests: totals.requests + row.requests
      }), { jobs: 0, fields: 0, chars: 0, requests: 0 })
    },
    retryHasJobs () {
      return this.retryTotals.requests > 0
    },
    summaryCards () {
      const totals = this.summary && this.summary.totals ? this.summary.totals : {}

      return [
        { label: 'Batchs', value: this.formatNumber(totals.batchCount || 0) },
        { label: 'Jobs', value: this.formatNumber(totals.jobCount || 0) },
        { label: 'Champs', value: this.formatNumber(totals.fieldCount || 0) },
        { label: 'En attente', value: this.formatNumber((this.summary && this.summary.processableBatchCount) || 0) }
      ]
    },
    workloadCards () {
      const pending = this.summary && this.summary.pendingTranslations ? this.summary.pendingTranslations : {}
      const pendingTotal = pending.total || {}
      const tracking = this.summary && this.summary.jobTracking ? this.summary.jobTracking : {}
      const trackingTotal = tracking.total || {}

      return [
        { label: 'Jobs restants', value: this.formatNumber(pendingTotal.jobCount || 0) },
        { label: 'Champs restants', value: this.formatNumber(pendingTotal.fieldCount || 0) },
        { label: 'Caractères source', value: this.formatNumber(pendingTotal.charCount || 0) },
        { label: 'Jobs à relancer', value: this.formatNumber(trackingTotal.retryableJobCount || 0) }
      ]
    },
    trackingByLocale () {
      const tracking = this.summary && this.summary.jobTracking ? this.summary.jobTracking : {}
      const rows = tracking.byLocale || []
      const indexedRows = {}

      for (let i = 0; i < rows.length; i++) {
        indexedRows[rows[i].locale] = rows[i]
      }

      return indexedRows
    },
    remainingLocaleRows () {
      const pending = this.summary && this.summary.pendingTranslations ? this.summary.pendingTranslations : {}
      const rows = pending.byLocale || []

      return rows.map(row => {
        const tracking = this.trackingByLocale[row.locale] || {}

        return {
          locale: row.locale,
          jobCount: row.jobCount || 0,
          fieldCount: row.fieldCount || 0,
          charCount: row.charCount || 0,
          activeJobCount: tracking.activeJobCount || 0,
          retryableJobCount: tracking.retryableJobCount || 0
        }
      })
    },
    remainingTypeRows () {
      const pending = this.summary && this.summary.pendingTranslations ? this.summary.pendingTranslations : {}

      if (!this.remainingTypeLocale) {
        return pending.byType || []
      }

      const locale = (pending.byLocale || []).find(row => row.locale === this.remainingTypeLocale)

      return locale ? (locale.types || []) : []
    },
    remainingTypeLocaleItems () {
      return [
        { value: '', label: 'Toutes les langues' },
        ...this.configuredLocales.map(locale => ({ value: locale, label: locale.toUpperCase() }))
      ]
    },
    configuredLocales () {
      const filters = this.summary && this.summary.configuredFilters ? this.summary.configuredFilters : {}

      return filters.locales || this.locales
    },
    configuredEntities () {
      const filters = this.summary && this.summary.configuredFilters ? this.summary.configuredFilters : {}

      return filters.entities || []
    },
    manualResultRows () {
      return this.manualResult && this.manualResult.rows ? this.manualResult.rows : []
    }
  },
  created () {
    this.refresh()
  },
  methods: {
    canRetryBatch (batch) {
      return batch.canRetry === true
    },
    async openBatchRetry (batch) {
      if (!this.canRetryBatch(batch) || this.isBatchBusy(batch)) {
        return
      }
      this.retryBatch = batch
      this.retryDialog = true
      this.retryResult = null
      this.retryError = ''
      this.retryLoading = true
      const requestId = ++this.retryRequestId

      try {
        const response = await Vue.prototype.$http.post(config.apiUrl + '/api/auto-translations/batches/preview', {
          retryBatchId: batch.id
        }, { headers: this.authHeaders() })

        if (requestId === this.retryRequestId) {
          this.retryResult = response.data
          this.updateRetryAvailability(batch, this.retryHasJobs)
        }
      } catch (error) {
        if (requestId === this.retryRequestId) {
          this.retryError = this.retryErrorMessage(error)
        }
      } finally {
        if (requestId === this.retryRequestId) {
          this.retryLoading = false
        }
      }
    },
    async submitBatchRetry () {
      if (this.retrySubmitting || this.retryLoading || this.retryError || !this.retryHasJobs || !this.retryBatch) {
        return
      }
      this.retrySubmitting = true
      this.retryError = ''

      try {
        const response = await Vue.prototype.$http.post(config.apiUrl + '/api/auto-translations/batches/generate', {
          retryBatchId: this.retryBatch.id
        }, { headers: this.authHeaders() })

        if (response.data.generated_count > 0) {
          this.updateRetryAvailability(this.retryBatch, false)
          this.retryDialog = false
          this.refresh()
        } else {
          this.retryResult = response.data
          this.updateRetryAvailability(this.retryBatch, false)
        }
      } catch (error) {
        this.retryError = this.retryErrorMessage(error)
      } finally {
        this.retrySubmitting = false
      }
    },
    retryErrorMessage (error) {
      const data = error.response && error.response.data

      return (data && data.violations && data.violations[0] && data.violations[0].title) || 'Impossible de préparer la relance.'
    },
    updateRetryAvailability (batch, canRetry) {
      this.batches = this.batches.map(item => item.id === batch.id ? { ...item, canRetry } : item)
      if (this.retryBatch && this.retryBatch.id === batch.id) {
        this.retryBatch = { ...this.retryBatch, canRetry }
      }
    },
    generateUniqueId () {
      return Date.now() + Math.random()
    },
    authHeaders () {
      return {
        Authorization: 'Bearer ' + this.$store.getters.getToken
      }
    },
    refresh () {
      this.page = 1
      this.hasMore = false
      this.batches = []
      this.getSummary()
      this.getBatches(false)
    },
    reset () {
      this.searchText = ''
      this.locale = null
      this.status = null
      this.refresh()
    },
    search () {
      this.page = 1
      this.hasMore = false
      this.batches = []
      this.getBatches(false)
    },
    getSummary () {
      this.summaryLoading = true

      Vue.prototype.$http
        .get(config.apiUrl + '/api/auto-translations/summary', {
          headers: this.authHeaders()
        })
        .then(response => {
          if (response.status === 200) {
            this.summary = response.data
          }
        })
        .finally(() => {
          this.summaryLoading = false
        })
    },
    getBatches (append) {
      this.loading = true

      const params = {
        page: this.page,
        limit: this.limit
      }

      if (this.searchText) {
        params.terms = this.searchText
      }

      if (this.locale) {
        params.locale = this.locale
      }

      if (this.status) {
        params.status = this.status
      }

      Vue.prototype.$http
        .get(config.apiUrl + '/api/auto-translations/batches', {
          headers: this.authHeaders(),
          params: params
        })
        .then(response => {
          if (response.status === 200) {
            const rows = response.data || []
            this.batches = append ? this.batches.concat(rows) : rows
            this.hasMore = rows.length === this.limit
            this.page = this.page + 1
          }
        })
        .finally(() => {
          this.loading = false
        })
    },
    manualPayload () {
      return {
        locale: this.manualForm.locale,
        entity: this.manualForm.entity,
        limit: Number(this.manualForm.limit || 0),
        maxBatches: 1,
        retryFailed: this.manualForm.retryFailed,
        oversized: this.manualForm.oversized
      }
    },
    previewManualBatch () {
      this.previewLoading = true

      Vue.prototype.$http
        .post(config.apiUrl + '/api/auto-translations/batches/preview', this.manualPayload(), {
          headers: this.authHeaders()
        })
        .then(response => {
          if (response.status === 200) {
            this.manualResult = response.data
          }
        })
        .finally(() => {
          this.previewLoading = false
        })
    },
    generateManualBatch () {
      this.generateLoading = true

      Vue.prototype.$http
        .post(config.apiUrl + '/api/auto-translations/batches/generate', this.manualPayload(), {
          headers: this.authHeaders()
        })
        .then(response => {
          if (response.status === 201) {
            this.manualResult = response.data
            this.refresh()
          }
        })
        .finally(() => {
          this.generateLoading = false
        })
    },
    refreshBatch (batch) {
      if (!this.canRefreshBatch(batch) || this.isBatchBusy(batch)) {
        return
      }
      this.setBatchActionLoading(batch, 'refresh', true)

      return Vue.prototype.$http
        .post(config.apiUrl + '/api/auto-translations/batches/' + batch.id + '/refresh', {}, {
          headers: this.authHeaders()
        })
        .then(response => {
          if (response.status === 200) {
            this.updateBatch(response.data.batch)
            this.getSummary()
          }
        })
        .finally(() => {
          this.setBatchActionLoading(batch, 'refresh', false)
        })
    },
    importBatch (batch) {
      if (!this.canImportBatch(batch) || this.isBatchBusy(batch)) {
        return
      }
      this.setBatchActionLoading(batch, 'import', true)

      return Vue.prototype.$http
        .post(config.apiUrl + '/api/auto-translations/batches/' + batch.id + '/import', {}, {
          headers: this.authHeaders()
        })
        .then(response => {
          if (response.status === 200) {
            this.refresh()
          }
        })
        .finally(() => {
          this.setBatchActionLoading(batch, 'import', false)
        })
    },
    updateBatch (updatedBatch) {
      if (!updatedBatch) {
        return
      }

      this.batches = this.batches.map(batch => batch.id === updatedBatch.id ? updatedBatch : batch)
    },
    setBatchActionLoading (batch, action, loading) {
      this.batchActionLoading = {
        ...this.batchActionLoading,
        [batch.id + ':' + action]: loading
      }
    },
    isBatchActionLoading (batch, action) {
      return this.batchActionLoading[batch.id + ':' + action] === true
    },
    isBatchBusy (batch) {
      return this.isBatchActionLoading(batch, 'refresh') || this.isBatchActionLoading(batch, 'import')
    },
    canRefreshBatch (batch) {
      return batch.canRefresh === true
    },
    canImportBatch (batch) {
      return batch.canImport === true
    },
    statusLabel (status) {
      const item = this.statusItems.find(item => item.value === status)

      return item ? item.label : (status || '-')
    },
    statusColor (status) {
      if (['imported', 'validated'].includes(status)) {
        return 'success'
      }

      if (status === 'partially_imported') {
        return 'warning'
      }

      if (['validation_failed', 'import_failed', 'failed', 'expired', 'cancelled', 'output_unavailable', 'error'].includes(status)) {
        return 'error'
      }

      if (['completed', 'downloaded'].includes(status)) {
        return 'info'
      }

      return 'warning'
    },
    formatNumber (value) {
      return new Intl.NumberFormat('fr-FR').format(value || 0)
    },
    formatDate (value) {
      if (!value) {
        return '-'
      }

      return new Intl.DateTimeFormat('fr-FR', {
        dateStyle: 'short',
        timeStyle: 'short'
      }).format(new Date(value))
    }
  }
}
</script>

<style lang="scss" scoped>
.summary-row {
  margin-top: -8px;
}

.summary-tile {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 6px;
  padding: 12px 14px;
  min-height: 72px;
}

.workload-tile {
  border-color: rgba(25, 118, 210, 0.22);
  background: rgba(25, 118, 210, 0.04);
}

.stats-panel {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 6px;
  overflow: hidden;
}

.manual-panel {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 6px;
  overflow: hidden;
}

.stats-title {
  padding: 10px 14px;
  font-size: 0.76rem;
  font-weight: 700;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.62);
  background: rgba(0, 0, 0, 0.025);
}

.batch-id {
  max-width: 360px;
  overflow-wrap: anywhere;
}

.retry-batch-id {
  overflow-wrap: anywhere;
}

.muted-text {
  color: rgba(0, 0, 0, 0.54);
  max-width: 420px;
  overflow-wrap: anywhere;
}
</style>
